import React from 'react';
import { ArrowRight, MapPin, Users, Clock, Award, Star, CheckCircle, Code, Brain, Target, Calendar, DollarSign } from 'lucide-react';
import { cppMockData } from './cpp-mock';
import { useLanguage } from '../../contexts/LanguageContext';
import { cppTranslations } from '../../translations/cpp-translations';

const CppHero = () => {
  const { hero, stats } = cppMockData;
  const { language } = useLanguage();
  const t = cppTranslations[language];
  
  return (
    <section className="cpp-hero">
      <div className="cpp-hero-content">
        <div className="cpp-hero-badge">
          <MapPin className="w-4 h-4" />
          {t.inPersonAnnouncement}
          <div className="ml-2 bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-bold">
            {language === 'ar' ? '🔥 الأكثر طلباً' : '🔥 MOST POPULAR'}
          </div>
        </div>
        
        {/* Rating Badge */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="flex items-center gap-1">
            {[1,2,3,4].map((star) => (
              <Star key={star} className="w-5 h-5 text-yellow-400 fill-current" />
            ))}
            <div className="relative">
              <Star className="w-5 h-5 text-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-current absolute top-0 left-0" style={{clipPath: 'inset(0 50% 0 0)'}} />
            </div>
          </div>
          <span className="text-gray-300">4.5/5 • {language === 'ar' ? '"مُعلّمي صبور، مُهتم، ويُسهّل فهم حتى أصعب المسائل. يُهيئ بيئةً إيجابيةً ومُلهمةً، ويُظهر لطفًا ودعمًا حقيقيًا. التعلّم معه مُثيرٌ دائمًا، وأشعر بالامتنان لوجودي بين هذا الشخص الرائع. ( موسى خطاب ) "' : '"My teacher is patient, caring, and makes even the hardest problems easy to understand. He creates a positive and inspiring environment, showing kindness and genuine support. Learning with him is always exciting, and I feel grateful to be surrounded by such a wonderful person. " Mousa Khattab'}</span>
        </div>
        
        <h1 className="cpp-heading-hero">
          {language === 'ar' ? (
            <>مسار C++: خطوتك الأولى<br />في عالم البرمجة</>
          ) : (
            <>C++ Pathway: Your First Step<br />into Coding</>
          )}
        </h1>
        
        <p className="cpp-body-large mb-8 max-w-3xl mx-auto">
          {language === 'ar' 
            ? 'لغة قوية تبني أساساً متيناً في البرمجة ومهارات حل المشكلات. ابدأ رحلتك البرمجية مع أقوى اللغات.'
            : 'A powerful language that builds strong programming foundations and problem-solving skills. Start your coding journey with one of the most robust languages.'
          }
        </p>

        {/* Key Selling Points */}
        <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
          <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <span className="text-sm text-gray-300">
              {language === 'ar' ? 'بدون خبرة مسبقة' : 'No Prior Experience'}
            </span>
          </div>
          <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <span className="text-sm text-gray-300">
              {language === 'ar' ? 'صفوف صغيرة (10 طالب)' : 'Small Classes (10 students)'}
            </span>
          </div>
          <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <span className="text-sm text-gray-300">
              {language === 'ar' ? 'حل المشكلات' : 'Problem-Solving'}
            </span>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-12">
          <button 
            onClick={() => {
              const contactSection = document.getElementById('contact');
              contactSection?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="cpp-btn-primary relative overflow-hidden group"
          >
            <span className="relative z-10 flex items-center">
              {language === 'ar' ? 'سجل الآن' : 'Enroll Now'}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </button>
          
          <button 
            onClick={() => {
              const curriculumSection = document.getElementById('curriculum');
              curriculumSection?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="cpp-btn-secondary"
          >
            {language === 'ar' ? 'عرض المنهج' : 'View Schedule'}
          </button>
        </div>
        
        {/* Enhanced Course Highlights */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto mb-12">
          {[
            { icon: Code, number: "30", label: language === 'ar' ? 'جلسة' : 'Sessions', color: "text-blue-400" },
            { icon: Clock, number: "70+", label: language === 'ar' ? 'ساعة' : 'Hours', color: "text-green-400" },
            { icon: Users, number: "10", label: language === 'ar' ? 'طلاب كحد أقصى' : 'Max Students', color: "text-orange-400" },
            { icon: Target, number: "50+", label: language === 'ar' ? 'حل مشاكل برمجية' : 'Problem Solving', color: "text-purple-400" }
          ].map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div key={index} className="cpp-card text-center group hover:scale-105 transition-transform">
                <IconComponent className={`w-8 h-8 ${stat.color} mx-auto mb-2`} />
                <div className="cpp-heading-2 text-white mb-2">{stat.number}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            )
          })}
        </div>
        
        {/* Enhanced Key Features Preview */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="cpp-card text-center group hover:border-purple-500 transition-colors">
            <Calendar className="w-8 h-8 text-purple-400 mx-auto mb-3" />
            <h3 className="cpp-heading-3 text-white mb-2">
              {language === 'ar' ? 'الجدول' : 'Schedule'}
            </h3>
            <p className="text-gray-400 text-sm">
              {language === 'ar' ? 'الأحد - الثلاثاء - الخميس' : 'Sunday - Tuesday - Thursday'}
            </p>
            <p className="text-gray-400 text-sm">
              {language === 'ar' ? '5:00 - 7:30 مساءً' : '5:00 - 7:30 PM'}
            </p>
          </div>
          
          <div className="cpp-card text-center group hover:border-green-500 transition-colors">
            <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-3" />
            <h3 className="cpp-heading-3 text-white mb-2">
              {language === 'ar' ? 'سعر مناسب' : 'Affordable Price'}
            </h3>
            <p className="text-gray-400 text-sm">
              {language === 'ar' ? '400 دينار أردني' : '400 JD Total'}
            </p>
            <p className="text-gray-400 text-sm">
              {language === 'ar' ? 'أو 160 دينار شهرياً' : 'or 160 JD per month'}
            </p>
          </div>

          <div className="cpp-card text-center group hover:border-blue-500 transition-colors">
            <Clock className="w-8 h-8 text-blue-400 mx-auto mb-3" />
            <h3 className="cpp-heading-3 text-white mb-2">
              {language === 'ar' ? 'يبدأ قريباً' : 'Starting Soon'}
            </h3>
            <p className="text-gray-400 text-sm">
              {language === 'ar' ? 'الأربعاء 17/9/2025' : 'Wednesday 17/9/2025'}
            </p>
            <p className="text-gray-400 text-sm">
              {language === 'ar' ? 'صف واحد فقط' : 'One class only'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CppHero;